import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import 'react-toastify/dist/ReactToastify.css';
import { findIndex, orderBy } from 'lodash';
import {
  fetchCreateVehicleService,
  fetchSetDefaultVehicleService,
  fetchDeleteVehicleService,
  fetchGetVehicleListService,
  fetchGetVehicleIrregularitiesByVehicleService,
} from '../../services/Vehicles';
import type {
  GetVehicleIrregularitiesByVehicleResponse,
  GetVehicleListResponse,
} from '../../services/Vehicles/types';
import { VehiclesStateType } from '../types';

const initialState: VehiclesStateType = {
  vehiclesLoading: false,
  vehicles: [],
  deleteVehicleIsOpen: false,
  createVehicleIsOpen: false,
};

export const fetchCreateVehicle = createAsyncThunk(
  'createVehicle',
  async (params: {
    driverId: string;
    licensePlate: string;
    category: string;
    brand: string;
  }) => {
    return await fetchCreateVehicleService(params);
  },
);

export const fetchSetDefaultVehicle = createAsyncThunk(
  'setDefaultVehicle',
  async (params: { driverId: string; vehicleId: string }) => {
    return await fetchSetDefaultVehicleService(params);
  },
);

export const fetchDeleteVehicle = createAsyncThunk(
  'deleteVehicle',
  async (params: { driverId: string; vehicleId: string }) => {
    return await fetchDeleteVehicleService(params);
  },
);

export const fetchGetVehicleList = createAsyncThunk(
  'getVehicleList',
  async (
    params: { driverId: string },
  ): Promise<{
    vehicles: GetVehicleListResponse['data'];
    irregularitiesByVehicle: GetVehicleIrregularitiesByVehicleResponse['data'];
  }> => {
    const vehiclesRes = await fetchGetVehicleListService(params);
    const irregularitiesRes =
      await fetchGetVehicleIrregularitiesByVehicleService(params);
    return {
      vehicles: vehiclesRes.data,
      irregularitiesByVehicle: irregularitiesRes.data,
    };
  },
);

export const vehiclesSlice = createSlice({
  name: 'vehicles',
  initialState,
  reducers: {
    toggleVehiclesLoading(state) {
      state.vehiclesLoading = !state.vehiclesLoading;
    },
    setVehicles(
      state,
      action: PayloadAction<
        {
          id: number;
          licensePlate: string;
          category: string;
          brand: string;
          default: number;
          used: number;
          irregularitiesCount?: number;
        }[]
      >,
    ) {
      return {
        ...state,
        vehicles: action.payload,
      };
    },
    toggleDeleteVehicleIsOpen(state) {
      state.deleteVehicleIsOpen = !state.deleteVehicleIsOpen;
    },
    toggleCreateVehicleIsOpen(state) {
      state.createVehicleIsOpen = !state.createVehicleIsOpen;
    },
    setUsedVehicle(state, action: PayloadAction<number>) {
      const idx = findIndex(state.vehicles, ['id', action.payload]);
      if (idx !== -1) {
        state.vehicles[idx].used = 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCreateVehicle.pending, (state) => {
      return {
        ...state,
        vehiclesLoading: true,
      };
    }),
      builder.addCase(fetchCreateVehicle.fulfilled, (state, res) => {
        return {
          ...state,
          vehiclesLoading: false,
        };
      }),
      builder.addCase(fetchCreateVehicle.rejected, (state, res) => {
        return {
          ...state,
          vehiclesLoading: false,
        };
      });

    builder.addCase(fetchSetDefaultVehicle.pending, (state) => {
      return {
        ...state,
        vehiclesLoading: true,
      };
    }),
      builder.addCase(fetchSetDefaultVehicle.fulfilled, (state, res) => {
        return {
          ...state,
          vehiclesLoading: false,
        };
      }),
      builder.addCase(fetchSetDefaultVehicle.rejected, (state, res) => {
        return {
          ...state,
          vehiclesLoading: false,
        };
      });

    builder.addCase(fetchDeleteVehicle.pending, (state) => {
      return {
        ...state,
        vehiclesLoading: true,
      };
    }),
      builder.addCase(fetchDeleteVehicle.fulfilled, (state, res) => {
        return {
          ...state,
          vehiclesLoading: false,
        };
      }),
      builder.addCase(fetchDeleteVehicle.rejected, (state, res) => {
        return {
          ...state,
          vehiclesLoading: false,
        };
      });

    builder.addCase(fetchGetVehicleList.pending, (state) => {
      return {
        ...state,
        vehiclesLoading: true,
      };
    }),
      builder.addCase(fetchGetVehicleList.fulfilled, (state, res) => {
        const { vehicles, irregularitiesByVehicle } = res.payload;

        const vehiclesWithIrregularities = vehicles.map((v) => {
          const match = irregularitiesByVehicle.find(
            (item) => item.licensePlate === v.licensePlate,
          );
          return {
            ...v,
            irregularitiesCount: match ? match.irregularities : 0,
          };
        });

        return {
          ...state,
          vehicles: orderBy(vehiclesWithIrregularities, ['default'], ['desc']),
          vehiclesLoading: false,
        };
      }),
      builder.addCase(fetchGetVehicleList.rejected, (state, res) => {
        return {
          ...state,
          vehiclesLoading: false,
        };
      });
  },
});

// Action creators are generated for each case reducer function
export const {
  toggleVehiclesLoading,
  toggleDeleteVehicleIsOpen,
  toggleCreateVehicleIsOpen,
  setUsedVehicle,
} = vehiclesSlice.actions;
